/**
 * Active-package resolution — VSCode-style "the package that owns the open file
 * is the one you see". Lifted into the framework (not per-surface), so the
 * viewer, Studio, and the website all switch packages by simply switching the
 * active file tab — no bespoke wiring each place.
 *
 * Derived purely from the VFS + the active file via viewer-core's package/space
 * discovery (`discoverRootsFromVfs` / `ownerOfPath` / `spaceOfPath`), so server
 * (commit trees) and web (in-memory VFS) agree on package boundaries.
 *
 *  - `rootPath`  — the package that owns the active file, as a VFS dir path
 *    ("/" for the repo-root/implicit package, "/svc/api" for a nested one). Fed
 *    to `useModel` so the shared model is scoped to that package (the opaque
 *    unit of resolution). `undefined` when nothing is open → caller's default.
 *  - `spaceKey`  — the SPACE that owns the active file (deepest space-or-package
 *    root). The stable key for per-space view state (remembered viewport). A
 *    package is itself a space, so this is defined whenever a file is open in a
 *    discovered tree.
 */
import { useMemo } from "react";
import {
  discoverRootsFromVfs,
  ownerOfPath,
  spaceOfPath,
  type Vfs,
} from "@archlang/viewer-core";

export interface ActivePackage {
  /** VFS dir path for `useModel`'s `rootPath`; `undefined` → default root. */
  readonly rootPath: string | undefined;
  /** Stable per-space key for view state; `null` when nothing is open. */
  readonly spaceKey: string | null;
}

/**
 * Resolve the active package + space from the workspace files and the active
 * file. Pass `explicitRoot` to pin the package (overrides the auto follow);
 * omit it for the default VSCode-style behaviour.
 */
export function useActivePackage(
  files: Vfs,
  activeFile: string | null,
  explicitRoot?: string,
): ActivePackage {
  return useMemo(() => {
    const discovery = discoverRootsFromVfs(files);
    const rootPath =
      explicitRoot !== undefined
        ? explicitRoot
        : activeFile
          ? toVfsDir(ownerOfPath(activeFile, discovery.packages)?.path)
          : undefined;
    const space = activeFile ? spaceOfPath(activeFile, discovery) : undefined;
    return { rootPath, spaceKey: space ? space.path : null };
  }, [files, activeFile, explicitRoot]);
}

/** Bare discovery path ("" | "svc/api") → VFS dir path ("/" | "/svc/api"). */
function toVfsDir(barePath: string | undefined): string | undefined {
  if (barePath === undefined) return undefined;
  return barePath === "" ? "/" : "/" + barePath;
}
